import { LANGUAGES_SUPPORTED, LanguagesSupported } from '@shared/types/enums';
import { LanguageInfo } from '@shared/types/language';
import { Route } from '@shared/types/routes';
import { PaginationState } from '@tanstack/react-table';
import { enUS, ja } from 'date-fns/locale';

// #region Routes
enum RoutesKey {
    home,
    publications,
    ranking,
    new,
    recently,
    books,
    magazines,
    magazineDetail,
    publicationDetail,
    reader,
    preview,
    forgotPassword,
    confirmPassword,
    verifyEmailChange,
    registrationStepOne,
    registrationStepTwo,
    registrationStepThree,
    comingSoon,
    profile,
    profileEdit,
    purchaseHistory,
    purchaseHistoryDetail,
    myLibraryPublications,
    myLibraryReadingHistory,
    myLibraryFavorites,
    usageGuide,
    privacyPolicy,
    termsOfService,
    cart,
    checkout,
    paymentResult,
    loginSSO
};

enum AdminRoutesKey {
    login,
    forgotPassword,
    confirmPassword,
    verifyEmailChange,
    profile,
    userSubscriptions,
    userManagement,
    planManagement,
    profileEdit
};

export const ROUTES: Record<keyof typeof RoutesKey, Route> = {
    home: {
        href: '/',
        title: 'words_title.home',
        menuLabel: 'words_title.home',
        breadcrumbLabel: 'words_title.home'
    },
    comingSoon: {
        href: '/coming-soon',
        title: '',
        menuLabel: '',
        breadcrumbLabel: ''
    },
    forgotPassword: {
        href: '/forgot-password',
        title: 'パスワード再発行',
        menuLabel: 'パスワード再発行'
    },
    confirmPassword: {
        href: '/set-password',
        title: 'パスワード設定',
        menuLabel: 'パスワード設定'
    },
    verifyEmailChange: {
        href: '/verify-email-change',
        title: 'メール変更確認',
        menuLabel: 'メール変更確認'
    },
    // #region Registration
    registrationStepOne: {
        href: '/registration/first-step',
        title: '新規アカウント登録',
        menuLabel: '',
        breadcrumbLabel: ''
    },
    registrationStepTwo: {
        href: '/registration/second-step',
        title: '新規アカウント登録',
        menuLabel: '',
        breadcrumbLabel: ''
    },
    registrationStepThree: {
        href: '/registration/third-step',
        title: '新規アカウント登録',
        menuLabel: '',
        breadcrumbLabel: ''
    },
    publications: {
        href: '/publications',
        title: '書籍・雑誌',
        menuLabel: '',
        breadcrumbLabel: ''
    },
    ranking: {
        href: '/publications/ranking',
        title: 'ランキング',
        menuLabel: '',
        breadcrumbLabel: ''
    },
    new: {
        href: '/publications/new',
        title: '新刊',
        menuLabel: '',
        breadcrumbLabel: ''
    },
    recently: {
        href: '/publications/recently',
        title: '最近',
        menuLabel: '',
        breadcrumbLabel: ''
    },
    books: {
        href: '/books',
        title: '書籍',
        menuLabel: '',
        breadcrumbLabel: ''
    },
    magazines: {
        href: '/magazines',
        title: '雑誌',
        menuLabel: '',
        breadcrumbLabel: ''
    },
    magazineDetail: {
        href: '/magazines/:id',
        title: '雑誌',
        menuLabel: '',
        breadcrumbLabel: ''
    },
    publicationDetail: {
        href: '/publications/:id',
        title: '雑誌',
        menuLabel: '',
        breadcrumbLabel: ''
    },
    reader: {
        href: '/reader/:id',
        title: '雑誌',
        menuLabel: '',
        breadcrumbLabel: ''
    },
    preview: {
        href: '/preview/:id',
        title: '雑誌',
        menuLabel: '',
        breadcrumbLabel: ''
    },
    profile: {
        href: '/profile',
        title: 'Profile',
        menuLabel: 'Profile',
        breadcrumbLabel: 'Profile'
    },
    profileEdit: {
        href: '/profile/edit',
        title: 'プロフィール編集',
        menuLabel: 'プロフィール編集',
        breadcrumbLabel: 'プロフィール編集'
    },
    purchaseHistory: {
        href: '/profile/purchase-history',
        title: '購入履歴',
        menuLabel: '購入履歴',
        breadcrumbLabel: '購入履歴'
    },
    purchaseHistoryDetail: {
        href: '/profile/purchase-history/:id',
        title: '購入履歴詳細',
        menuLabel: '購入履歴詳細',
        breadcrumbLabel: '購入履歴詳細'
    },
    myLibraryPublications: {
        href: '/profile/my-library/publications',
        title: '購入済み書籍・雑誌',
        menuLabel: '購入済み書籍・雑誌',
        breadcrumbLabel: '購入済み書籍・雑誌'
    },
    myLibraryReadingHistory: {
        href: '/profile/my-library/reading-history',
        title: '読書履歴',
        menuLabel: '読書履歴',
        breadcrumbLabel: '読書履歴'
    },
    myLibraryFavorites: {
        href: '/profile/my-library/favorites',
        title: 'お気に入り',
        menuLabel: 'お気に入り',
        breadcrumbLabel: 'お気に入り'
    },
    usageGuide: {
        href: '/usage-guide',
        title: '利用ガイド',
        menuLabel: '利用ガイド',
        breadcrumbLabel: '利用ガイド'
    },
    privacyPolicy: {
        href: '/privacy-policy',
        title: '個人情報保護方針',
        menuLabel: '個人情報保護方針',
        breadcrumbLabel: '個人情報保護方針'
    },
    termsOfService: {
        href: '/terms-of-service',
        title: '利用規約',
        menuLabel: '利用規約',
        breadcrumbLabel: '利用規約'
    },
    cart: {
        href: '/cart',
        title: 'マイページ',
        menuLabel: 'カート',
        breadcrumbLabel: 'カート'
    },
    checkout: {
        href: '/checkout',
        title: 'お支払い手続き',
        menuLabel: 'お支払い手続き',
        breadcrumbLabel: 'お支払い手続き'
    },
    paymentResult: {
        href: '/checkout/order/:id/payment-result',
        title: 'お支払い完了',
        menuLabel: 'お支払い完了',
        breadcrumbLabel: 'お支払い完了'
    },
    loginSSO: {
        href: '/login-sso',
        title: 'SSO Login',
        menuLabel: '',
        breadcrumbLabel: ''
    }
};

export const ADMIN_ROUTES: Record<keyof typeof AdminRoutesKey, Route> = {
    login: {
        href: '/login',
        title: 'ログイン',
        menuLabel: '',
        breadcrumbLabel: ''
    },
    forgotPassword: {
        href: '/forgot-password',
        title: 'パスワードを忘れた方はこちら',
        menuLabel: 'パスワードを忘れた方はこちら'
    },
    confirmPassword: {
        href: '/set-password',
        title: 'パスワード設定',
        menuLabel: 'パスワード設定'
    },
    verifyEmailChange: {
        href: '/verify-email-change',
        title: 'メール変更確認',
        menuLabel: 'メール変更確認'
    },
    profile: {
        href: '/profile',
        title: 'マイページ',
        menuLabel: '',
        breadcrumbLabel: ''
    },
    userSubscriptions: {
        href: '/subscriptions',
        title: 'ユーザー定期購読一覧',
        menuLabel: '',
        breadcrumbLabel: ''
    },
    userManagement: {
        href: '/users',
        title: 'ユーザー管理',
        menuLabel: '',
        breadcrumbLabel: ''
    },
    planManagement: {
        href: '/plans',
        title: 'プラン管理',
        menuLabel: '',
        breadcrumbLabel: ''
    },
    profileEdit: {
        href: '/profile/edit',
        title: 'プロフィール編集',
        menuLabel: '',
        breadcrumbLabel: ''
    }
};

// #endregion Routes

// #region Languages
export const DEFAULT_LANGUAGE: LanguagesSupported = LANGUAGES_SUPPORTED.English;

export const LANGUAGES_MAPPER: {
    japanese: LanguageInfo,
    english: LanguageInfo
} = {
    english: {
        code: LANGUAGES_SUPPORTED.English,
        fullname: 'words_title.english',
        shortName: 'EN',
        flag: 'gb',
        dateFns: enUS
    },
    japanese: {
        code: LANGUAGES_SUPPORTED.Japanese,
        fullname: 'words_title.japanese',
        shortName: 'JP',
        flag: 'jp',
        dateFns: ja
    }
};

// #endredion

// #region Dates

export const DATE_YMD = 'yyyy/MM/dd';

// #endregion

// #region Table

export const DEFAULT_SELECT_PAGE_SIZES: number[] = [10, 20, 50, 100, 200, 500, 1000];

export const DEFAULT_PAGINATION: PaginationState = {
    pageIndex: 0,
    pageSize: 5
};

// #endregion

// #region System Content Types

export const SYSTEM_CONTENT_TYPE = {
    USER_GUIDE: 'USER_GUIDE',
    TERMS_OF_SERVICE: 'TERMS_OF_SERVICE',
    PRIVACY_POLICY: 'PRIVACY_POLICY'
} as const;

// #endregion

export const SCREEN_TYPE = {
    CMS: 'CMS',
    GU: 'GU',
    GA: 'GA'
} as const;
